import * as React from 'react';
import 'intersection-observer';

import Spinner from 'components/spinner/Spinner';
import ActionLink from 'components/buttons/action-link/ActionLink';

import { logError } from 'utils/log';
import { __t, applyTranslations } from 'utils/language';

import './infinite-load-indicator.scss';

applyTranslations(require('./infinite-load-indicator.i18n.json'));

type Props = {
  isLoading: boolean,
  triggerImmediatelyOnMount?: boolean,
  detectionOffset: number,
  onLoadMore: () => void
};

class InfiniteScrollIndicator extends React.PureComponent<Props> {
  static defaultProps = {
    detectionOffset: 0
  };

  observer: IntersectionObserver | null = null;
  indicator = React.createRef<HTMLDivElement>();

  componentDidMount(): void {
    try {
      if (this.indicator.current) {
        this.observer = new IntersectionObserver(this.handleIntersection); // eslint-disable-line no-undef
        this.observer.observe(this.indicator.current);
      }
    } catch (e) {
      logError(e);
    }
  }

  componentWillUnmount(): void {
    try {
      if (this.observer) {
        this.observer.disconnect();
      }
    } catch (e) {
      logError(e);
    }
  }

  handleIntersection = (entries: Array<IntersectionObserverEntry>) => {
    if (this.props.triggerImmediatelyOnMount || entries.some((entry) => entry.intersectionRatio > 0)) {
      // run only if intersecting with viewport
      this.handleLoadMore();
    }
  };

  handleLoadMore = () => {
    const { isLoading, onLoadMore } = this.props;

    if (!isLoading) {
      onLoadMore();
    }
  };

  render() {
    const { isLoading, detectionOffset } = this.props;

    return (
      <section>
        <div
          style={{ transform: `translateY(-${detectionOffset}px)`, width: 2, height: 2 }}
          ref={this.indicator}
        />
        <div
          style={{ display: isLoading ? 'block' : 'none' }}
        >
          <Spinner
            position="inline"
            size="small"
            color="light"
          />
        </div>
        <div
          className="infinite-load-indicator__text-link"
          style={{ display: isLoading ? 'none' : 'block' }}
        >
          <ActionLink onClick={this.handleLoadMore}>
            {__t('infinite_load_indicator.load_more')}
          </ActionLink>
        </div>
      </section>
    );
  }
}

export default InfiniteScrollIndicator;
